// Función para extraer el título del markdown (primer h1)
export function extractTitle(markdownContent) {
  const match = markdownContent.match(/^#\s+(.+)$/m);
  return match ? match[1].trim() : 'Sin título';
}

// Función para procesar el contenido completo del markdown mezclando texto, diagramas y código
export function parseMarkdownWithDiagrams(markdownContent) {
  const sections = [];
  let currentPosition = 0;

  // Regex para detectar bloques de código con o sin lenguaje específico
  const codeBlockRegex = /```(\w*)\s*\n([\s\S]*?)```/g;
  let match;

  // Encontrar todos los bloques de código (mermaid, dart, javascript, etc.)
  const codeBlocks = [];
  while ((match = codeBlockRegex.exec(markdownContent)) !== null) {
    const language = match[1] ? match[1].toLowerCase() : '';
    codeBlocks.push({
      language: language || 'text',
      code: match[2].trim(),
      start: match.index,
      end: match.index + match[0].length,
      type: language === 'mermaid' ? 'mermaid' : 'code'
    });
  }

  // Ordenar bloques por posición
  codeBlocks.sort((a, b) => a.start - b.start);

  // Construir secciones alternando texto, diagramas y código
  codeBlocks.forEach((block, index) => {
    // Agregar texto antes del bloque
    const textBefore = markdownContent.substring(currentPosition, block.start);
    if (textBefore.trim()) {
      sections.push({
        type: 'text',
        content: textBefore.trim()
      });
    }

    // Agregar el bloque (mermaid o código)
    if (block.type === 'mermaid') {
      sections.push({
        type: 'mermaid',
        content: block.code
      });
    } else {
      sections.push({
        type: 'code',
        language: block.language,
        content: block.code
      });
    }

    currentPosition = block.end;
  });

  // Agregar texto después del último bloque (si hay bloques)
  if (codeBlocks.length > 0) {
    const textAfter = markdownContent.substring(currentPosition);
    if (textAfter.trim()) {
      sections.push({
        type: 'text',
        content: textAfter.trim()
      });
    }
  }

  // Si no hay bloques de código y no se agregó nada, todo es texto
  if (sections.length === 0 && markdownContent.trim()) {
    sections.push({
      type: 'text',
      content: markdownContent.trim()
    });
  }

  return sections;
}

// Función para renderizar markdown simple a HTML
export function markdownToHtml(markdown) {
  if (!markdown) return '';

  let html = markdown;

  // Dividir en líneas
  const lines = html.split('\n');
  const processedLines = [];
  let inList = false;
  let inTable = false;
  let tableRows = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Detectar si es una línea de tabla
    const isTableRow = line.trim().startsWith('|') && line.trim().endsWith('|');
    const isTableSeparator = isTableRow && /^\|\s*[-:]+\s*\|/.test(line.trim());

    // Si estábamos en una tabla y ya no estamos, renderizar la tabla
    if (inTable && !isTableRow) {
      processedLines.push(renderTable(tableRows));
      tableRows = [];
      inTable = false;
    }

    // Ignorar líneas con solo guiones (separadores ---)
    if (line.trim() === '---' || line.trim() === '- - -') {
      continue;
    }

    // Procesar líneas de tabla
    if (isTableRow) {
      if (!isTableSeparator) {
        tableRows.push(line.trim());
      }
      inTable = true;
      continue;
    }

    // Títulos (procesar de más específico a menos específico)
    if (line.startsWith('###### ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h6>${processInlineFormatting(line.substring(7))}</h6>`);
    } else if (line.startsWith('##### ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h5>${processInlineFormatting(line.substring(6))}</h5>`);
    } else if (line.startsWith('#### ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h4>${processInlineFormatting(line.substring(5))}</h4>`);
    } else if (line.startsWith('### ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h3>${processInlineFormatting(line.substring(4))}</h3>`);
    } else if (line.startsWith('## ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h2>${processInlineFormatting(line.substring(3))}</h2>`);
    } else if (line.startsWith('# ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(`<h1>${processInlineFormatting(line.substring(2))}</h1>`);
    }
    // Listas
    else if (line.startsWith('- ')) {
      if (!inList) {
        processedLines.push('<ul>');
        inList = true;
      }
      let listItem = line.substring(2);
      // Procesar formato inline en lista
      listItem = processInlineFormatting(listItem);
      processedLines.push(`<li>${listItem}</li>`);
    }
    // Línea vacía
    else if (line.trim() === '') {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push('');
    }
    // Párrafo normal
    else {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      // Procesar formato inline
      line = processInlineFormatting(line);
      processedLines.push(`<p>${line}</p>`);
    }
  }

  // Cerrar lista si quedó abierta
  if (inList) {
    processedLines.push('</ul>');
  }

  // Cerrar tabla si quedó abierta
  if (inTable) {
    processedLines.push(renderTable(tableRows));
  }

  html = processedLines.join('\n');

  return html;
}

// Función auxiliar para procesar formato inline (negrita, cursiva, enlaces, etc.)
function processInlineFormatting(text) {
  // Procesar enlaces [texto](url) PRIMERO, para no interferir con otros formatos.
  // - Enlaces http(s): se abren en una pestaña nueva.
  // - Enlaces a otros documentos (.md): se convierten a la ruta interna /docs/<id>
  //   (el id es el nombre del archivo sin .md, con guiones en vez de guiones bajos).
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, rawUrl) => {
    const url = rawUrl.trim();
    if (/^https?:\/\//i.test(url)) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
    const mdMatch = url.match(/([^/\\]+)\.md(#.*)?$/i);
    if (mdMatch) {
      const id = mdMatch[1].replace(/_/g, '-').toLowerCase();
      return `<a href="/docs/${id}">${label}</a>`;
    }
    return `<a href="${url}">${label}</a>`;
  });
  // Procesar negrita **texto**
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Procesar cursiva *texto*
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Procesar código `texto`
  text = text.replace(/`(.*?)`/g, '<code>$1</code>');
  return text;
}

// Función auxiliar para renderizar una tabla
function renderTable(rows) {
  if (rows.length === 0) return '';

  let tableHtml = '<table class="markdown-table">\n';

  // Primera fila es el header
  if (rows.length > 0) {
    const headerCells = rows[0].split('|').filter(cell => cell.trim() !== '');
    tableHtml += '  <thead>\n    <tr>\n';
    headerCells.forEach(cell => {
      const processedCell = processInlineFormatting(cell.trim());
      tableHtml += `      <th>${processedCell}</th>\n`;
    });
    tableHtml += '    </tr>\n  </thead>\n';
  }

  // Resto de las filas son el body
  if (rows.length > 1) {
    tableHtml += '  <tbody>\n';
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i].split('|').filter(cell => cell.trim() !== '');
      tableHtml += '    <tr>\n';
      cells.forEach(cell => {
        const processedCell = processInlineFormatting(cell.trim());
        tableHtml += `      <td>${processedCell}</td>\n`;
      });
      tableHtml += '    </tr>\n';
    }
    tableHtml += '  </tbody>\n';
  }

  tableHtml += '</table>';

  return tableHtml;
}
